import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button } from '@/components/atoms';
import {
  useSettingsList,
  useAttachmentDownloadUrl,
} from '@/hooks/useErpApi';
import {
  parseAddress,
  parseBankDetails,
  type Address,
  type BankDetails,
} from '@/schemas/settings';

export default function FPOProfilePage() {
  const navigate = useNavigate();

  // Load all settings - same API call as settings page
  const { data: settingsData, isLoading: isLoadingSettings } = useSettingsList();
  
  // Extract individual settings from the list
  const settings = useMemo(() => {
    if (!settingsData) return [];
    if (Array.isArray(settingsData)) {
      return settingsData;
    }
    if ((settingsData as any)?.data && Array.isArray((settingsData as any).data)) {
      return (settingsData as any).data;
    }
    return [];
  }, [settingsData]);
  
  const getSetting = useCallback((key: string): string | null => {
    const setting = settings.find((s: any) => s.key === key);
    return setting?.value || null;
  }, [settings]);

  // Get FPO data
  const fpoName = getSetting('fpo_name') || 'Not Set';
  const logoAttachmentId = getSetting('fpo_logo_attachment_id');
  
  // Get logo URL - same API call as settings page
  const { data: logoUrlData } = useAttachmentDownloadUrl(logoAttachmentId || '', {
    enabled: !!logoAttachmentId,
  });

  // Parse addresses and bank details
  const branchAddressValue = getSetting('fpo_branch_address');
  const branchAddress: Address | null = branchAddressValue ? parseAddress(branchAddressValue) : null;

  const registeredAddressValue = getSetting('fpo_registered_address');
  const registeredAddress: Address | null = registeredAddressValue ? parseAddress(registeredAddressValue) : null;

  const bankDetailsValue = getSetting('fpo_bank_details');
  const bankDetails: BankDetails | null = bankDetailsValue ? parseBankDetails(bankDetailsValue) : null;

  // Get logo URL from hook response
  const logoUrl = useMemo(() => {
    if (!logoUrlData) return null;
    return (logoUrlData as any)?.data?.download_url || (logoUrlData as any)?.data?.url || null;
  }, [logoUrlData]);

  // Format address for display
  const formatAddress = (address: Address | null): string => {
    if (!address) return 'Not Set';
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.district,
      address.state,
      address.pincode,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-lg font-medium text-neutral-600">Loading FPO profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">FPO Profile</h1>
            <p className="text-neutral-600 mt-2">
              View your organization's profile information
            </p>
          </div>
        </div>
        <Button variant="primary" onClick={() => navigate('/settings')}>
          <Icon name="Settings" size={20} className="mr-2" />
          Edit Settings
        </Button>
      </div>

      {/* FPO Logo and Name Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Logo */}
          <div className="flex-shrink-0">
            {logoUrl ? (
              <div className="relative">
                <img
                  src={logoUrl}
                  alt="FPO Logo"
                  className="h-32 w-32 object-contain rounded-lg border-2 border-neutral-200 bg-white p-2"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="h-32 w-32 rounded-lg border-2 border-neutral-200 bg-neutral-50 flex items-center justify-center">
                <Icon name="Image" size={48} className="text-neutral-400" />
              </div>
            )}
          </div>

          {/* FPO Name */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">{fpoName}</h2>
            <p className="text-sm text-neutral-600">Farmer Producer Organization</p>
          </div>
        </div>
      </div>

      {/* Company Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary-50 p-2 rounded-lg">
            <Icon name="Building" size={20} className="text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Company Information</h3>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-neutral-500 mb-1">Organization Name</p>
            <p className="text-sm font-medium text-neutral-900">{fpoName}</p>
          </div>
        </div>
      </div>

      {/* Branch Address Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-info-50 p-2 rounded-lg">
            <Icon name="MapPin" size={20} className="text-info-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Branch Address</h3>
        </div>
        {branchAddress ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {branchAddress.line1 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Address Line 1</p>
                  <p className="text-sm text-neutral-900">{branchAddress.line1}</p>
                </div>
              )}
              {branchAddress.line2 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Address Line 2</p>
                  <p className="text-sm text-neutral-900">{branchAddress.line2}</p>
                </div>
              )}
              {branchAddress.city && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">City</p>
                  <p className="text-sm text-neutral-900">{branchAddress.city}</p>
                </div>
              )}
              {branchAddress.district && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">District</p>
                  <p className="text-sm text-neutral-900">{branchAddress.district}</p>
                </div>
              )}
              {branchAddress.state && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">State</p>
                  <p className="text-sm text-neutral-900">{branchAddress.state}</p>
                </div>
              )}
              {branchAddress.pincode && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Pincode</p>
                  <p className="text-sm text-neutral-900 font-mono">{branchAddress.pincode}</p>
                </div>
              )}
              {branchAddress.country && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Country</p>
                  <p className="text-sm text-neutral-900">{branchAddress.country}</p>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 mb-1">Full Address</p>
              <p className="text-sm text-neutral-900">{formatAddress(branchAddress)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic">Branch address not configured</p>
        )}
      </div>

      {/* Registered Address Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-success-50 p-2 rounded-lg">
            <Icon name="FileText" size={20} className="text-success-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Registered Address</h3>
        </div>
        {registeredAddress ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {registeredAddress.line1 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Address Line 1</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.line1}</p>
                </div>
              )}
              {registeredAddress.line2 && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Address Line 2</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.line2}</p>
                </div>
              )}
              {registeredAddress.city && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">City</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.city}</p>
                </div>
              )}
              {registeredAddress.district && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">District</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.district}</p>
                </div>
              )}
              {registeredAddress.state && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">State</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.state}</p>
                </div>
              )}
              {registeredAddress.pincode && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Pincode</p>
                  <p className="text-sm text-neutral-900 font-mono">{registeredAddress.pincode}</p>
                </div>
              )}
              {registeredAddress.country && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Country</p>
                  <p className="text-sm text-neutral-900">{registeredAddress.country}</p>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 mb-1">Full Address</p>
              <p className="text-sm text-neutral-900">{formatAddress(registeredAddress)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic">Registered address not configured</p>
        )}
      </div>

      {/* Bank Details Card */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-warning-50 p-2 rounded-lg">
            <Icon name="IndianRupee" size={20} className="text-warning-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Bank Details</h3>
        </div>
        {bankDetails ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bankDetails.bank_name && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Bank Name</p>
                <p className="text-sm font-medium text-neutral-900">{bankDetails.bank_name}</p>
              </div>
            )}
            {bankDetails.account_number && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Account Number</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{bankDetails.account_number}</p>
              </div>
            )}
            {bankDetails.ifsc_code && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">IFSC Code</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{bankDetails.ifsc_code}</p>
              </div>
            )}
            {bankDetails.branch_name && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Branch Name</p>
                <p className="text-sm text-neutral-900">{bankDetails.branch_name}</p>
              </div>
            )}
            {bankDetails.account_type && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Account Type</p>
                <p className="text-sm text-neutral-900 capitalize">{bankDetails.account_type}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic">Bank details not configured</p>
        )}
      </div>
    </div>
  );
}



